/**
 * useHRData Hook
 * Manages data fetching and state for HR dashboard
 */

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { isRecruitingRole } from '../utils/roleUtils';
import * as hrService from '../services/hrService';

export const useHRData = (includeOlderJobs = false) => {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  /**
   * Fetch all data in parallel
   */
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    
    try {
      const results = await Promise.all([
        hrService.fetchSubmissions(),
        hrService.fetchCandidates(),
        hrService.fetchJobs(includeOlderJobs),
        hrService.fetchClients(),
        hrService.fetchRecruiters()
      ]);

      const [submissionsResult, candidatesResult, jobsResult, clientsResult, recruitersResult] = results;

      // Handle submissions
      if (submissionsResult.success) {
        setSubmissions(submissionsResult.data);
      } else {
        setSubmissions([]);
        if (submissionsResult.error === 'Timeout') {
          toast.error('Loading submissions timed out. Showing limited data.');
        }
      }

      // Handle candidates
      if (candidatesResult.success) {
        setCandidates(candidatesResult.data);
      } else {
        setCandidates([]);
        if (candidatesResult.error === 'Timeout') {
          toast.error('Loading candidates is slow. Showing limited data.');
        }
      }

      // Handle jobs
      if (jobsResult.success) {
        setJobs(jobsResult.data);
      } else {
        setJobs([]);
        if (jobsResult.error === 'Timeout') {
          toast.error('Loading jobs is slow. Showing limited data.');
        }
      }

      // Handle clients
      if (clientsResult.success) {
        setClients(clientsResult.data);
      } else {
        setClients([]);
      }

      // Handle recruiters
      if (recruitersResult.success) {
        const recruiterUsers = recruitersResult.data.filter(u => isRecruitingRole(u.role));
        setRecruiters(recruiterUsers);
      } else {
        setRecruiters([]);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [includeOlderJobs]);

  // Fetch data on mount and when includeOlderJobs changes
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    loading,
    submissions,
    candidates,
    jobs,
    clients,
    recruiters,
    refetch: fetchAllData
  };
};

export default useHRData;
